import * as THREE from 'three';
import earcut from 'earcut';

/**
 * Boundary helpers for the outward-thickness pipeline.
 *
 * Isolated from the existing thickness/boundaryUtils so this new pipeline
 * can be modified or removed without touching the legacy implementation.
 */

function edgeKey(a, b) {
  return a < b ? `${a}_${b}` : `${b}_${a}`;
}

/**
 * Find boundary edges of an indexed (or implicit triple) triangle mesh.
 * Returns array of [a, b] pairs - each pair belongs to exactly one triangle.
 */
export function findBoundaryEdges(geometry) {
  const indices = geometry.index ? geometry.index.array : null;
  const vertexCount = geometry.attributes.position.count;

  const edgeCount = new Map();
  const edgeFirst = new Map(); // edgeKey -> [a,b] preserving observed orientation

  const addEdge = (a, b) => {
    const key = edgeKey(a, b);
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
    if (!edgeFirst.has(key)) edgeFirst.set(key, [a, b]);
  };

  if (indices) {
    for (let i = 0; i < indices.length; i += 3) {
      addEdge(indices[i], indices[i + 1]);
      addEdge(indices[i + 1], indices[i + 2]);
      addEdge(indices[i + 2], indices[i]);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      addEdge(i, i + 1);
      addEdge(i + 1, i + 2);
      addEdge(i + 2, i);
    }
  }

  const boundary = [];
  for (const [key, count] of edgeCount) {
    if (count === 1) {
      boundary.push(edgeFirst.get(key));
    }
  }
  return boundary;
}

/**
 * Build ordered rim loops from boundary edges (edge-walk, not vertex-greedy).
 * Returns array of loops; each loop is an ordered array of vertex indices.
 */
export function buildRimLoops(boundaryEdges) {
  if (!boundaryEdges?.length) return [];

  const boundaryAdjacency = new Map();
  for (const [a, b] of boundaryEdges) {
    if (!boundaryAdjacency.has(a)) boundaryAdjacency.set(a, []);
    if (!boundaryAdjacency.has(b)) boundaryAdjacency.set(b, []);
    boundaryAdjacency.get(a).push(b);
    boundaryAdjacency.get(b).push(a);
  }

  const visitedEdges = new Set();
  const visitEdge = (a, b) => visitedEdges.add(edgeKey(a, b));
  const isEdgeVisited = (a, b) => visitedEdges.has(edgeKey(a, b));

  const loops = [];
  for (const [a, b] of boundaryEdges) {
    if (isEdgeVisited(a, b)) continue;

    const loop = [a];
    visitEdge(a, b);
    let prev = a;
    let current = b;
    loop.push(current);

    const safetyLimit = boundaryEdges.length * 2 + 8;
    let steps = 0;
    while (steps++ < safetyLimit) {
      const neighbors = boundaryAdjacency.get(current) || [];
      let next = null;
      for (const n of neighbors) {
        if (n === prev) continue;
        if (!isEdgeVisited(current, n)) {
          next = n;
          break;
        }
      }
      if (next == null) {
        if (neighbors.includes(loop[0]) && !isEdgeVisited(current, loop[0])) {
          visitEdge(current, loop[0]);
        }
        break;
      }
      visitEdge(current, next);
      loop.push(next);
      prev = current;
      current = next;
      if (current === loop[0]) break;
    }

    if (loop.length >= 3) {
      if (loop[loop.length - 1] === loop[0]) loop.pop();
      loops.push(loop);
    }
  }

  return loops;
}

/** Count edges shared by more than two triangles (non-manifold). */
export function countNonManifoldEdges(geometry) {
  if (!geometry?.index) return 0;
  const indices = geometry.index.array;
  const edgeCount = new Map();
  const addEdge = (a, b) => {
    const key = edgeKey(a, b);
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
  };
  for (let i = 0; i < indices.length; i += 3) {
    addEdge(indices[i], indices[i + 1]);
    addEdge(indices[i + 1], indices[i + 2]);
    addEdge(indices[i + 2], indices[i]);
  }
  let n = 0;
  for (const c of edgeCount.values()) {
    if (c > 2) n++;
  }
  return n;
}

/**
 * Compute an approximate plane (centroid + normal) for a rim loop using
 * Newell's method.
 */
export function loopPlane(loop, positions) {
  const centroid = new THREE.Vector3();
  for (const i of loop) {
    centroid.x += positions[i * 3];
    centroid.y += positions[i * 3 + 1];
    centroid.z += positions[i * 3 + 2];
  }
  centroid.divideScalar(loop.length || 1);

  const normal = new THREE.Vector3();
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  for (let k = 0; k < loop.length; k++) {
    const ci = loop[k];
    const ni = loop[(k + 1) % loop.length];
    a.set(positions[ci * 3], positions[ci * 3 + 1], positions[ci * 3 + 2]);
    b.set(positions[ni * 3], positions[ni * 3 + 1], positions[ni * 3 + 2]);
    normal.x += (a.y - b.y) * (a.z + b.z);
    normal.y += (a.z - b.z) * (a.x + b.x);
    normal.z += (a.x - b.x) * (a.y + b.y);
  }
  if (normal.lengthSq() < 1e-12) normal.set(0, 1, 0);
  normal.normalize();
  return { centroid, normal };
}

/**
 * Triangulate one rim loop using earcut on a 2D projection of the loop plane.
 * Returns an array of triangle index triplets that reference the original
 * mesh vertex indices (so the cap shares verts with the body and is sealed).
 */
export function triangulateLoopCap(loop, positions, planeNormal) {
  if (loop.length < 3) return [];

  // Build a 2D basis on the plane
  const n = planeNormal.clone().normalize();
  const tmp = Math.abs(n.x) < 0.9
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  const u = new THREE.Vector3().crossVectors(n, tmp).normalize();
  const v = new THREE.Vector3().crossVectors(n, u).normalize();

  const flat = new Array(loop.length * 2);
  for (let k = 0; k < loop.length; k++) {
    const idx = loop[k];
    const x = positions[idx * 3];
    const y = positions[idx * 3 + 1];
    const z = positions[idx * 3 + 2];
    flat[k * 2] = x * u.x + y * u.y + z * u.z;
    flat[k * 2 + 1] = x * v.x + y * v.y + z * v.z;
  }

  const tri = earcut(flat); // indices into `loop`
  const out = [];
  for (let k = 0; k < tri.length; k += 3) {
    out.push([loop[tri[k]], loop[tri[k + 1]], loop[tri[k + 2]]]);
  }
  return out;
}

/**
 * Compute (and cache) area-weighted vertex normals as a Float32Array.
 * Independent of any existing implementation in src/components/tool_handlers/thickness.
 */
export function computeVertexNormalsArray(geometry) {
  const positions = geometry.attributes.position.array;
  const indices = geometry.index ? geometry.index.array : null;
  const vertexCount = positions.length / 3;
  const normals = new Float32Array(vertexCount * 3);

  const accumulate = (i0, i1, i2) => {
    const ax = positions[i0 * 3], ay = positions[i0 * 3 + 1], az = positions[i0 * 3 + 2];
    const bx = positions[i1 * 3], by = positions[i1 * 3 + 1], bz = positions[i1 * 3 + 2];
    const cx = positions[i2 * 3], cy = positions[i2 * 3 + 1], cz = positions[i2 * 3 + 2];
    const ex = bx - ax, ey = by - ay, ez = bz - az;
    const fx = cx - ax, fy = cy - ay, fz = cz - az;
    const nx = ey * fz - ez * fy;
    const ny = ez * fx - ex * fz;
    const nz = ex * fy - ey * fx;
    normals[i0 * 3]     += nx; normals[i0 * 3 + 1] += ny; normals[i0 * 3 + 2] += nz;
    normals[i1 * 3]     += nx; normals[i1 * 3 + 1] += ny; normals[i1 * 3 + 2] += nz;
    normals[i2 * 3]     += nx; normals[i2 * 3 + 1] += ny; normals[i2 * 3 + 2] += nz;
  };

  if (indices) {
    for (let i = 0; i < indices.length; i += 3) {
      accumulate(indices[i], indices[i + 1], indices[i + 2]);
    }
  } else {
    for (let i = 0; i < vertexCount; i += 3) {
      accumulate(i, i + 1, i + 2);
    }
  }

  for (let i = 0; i < vertexCount; i++) {
    const x = normals[i * 3], y = normals[i * 3 + 1], z = normals[i * 3 + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len > 1e-12) {
      normals[i * 3]     = x / len;
      normals[i * 3 + 1] = y / len;
      normals[i * 3 + 2] = z / len;
    }
  }
  return normals;
}
